import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import helperMarkdownToComponent from 'helpers/Data';
import { getCommonTealiumProps } from 'helpers/AccountHelper';
import { handleAnalytics, Events, SegmentPageProfileTypes } from '@foxcorp/delta-web-analytics';
import OverlayFooter from 'components/OverlayFooter';
import Header from '../Header';

import Style from './CreatePassword.module.scss';

const CREATE_PASSWORD = 'Create Password';

const CreatePassword = ({
  createPasswordGenericError = '',
  createPasswordNewPassword = '',
  createPasswordNewPasswordError = null,
  createPasswordPasswordError = null,
  createPasswordShowNewPassword = null,
  googleLinkingError = '',
  headerProps = {},
  language = {
    profile_currentPwInputPlaceholder: '',
    profile_newPwInputPlaceholder: '',
    profile_passwordRequirementsText: '',
    profileEdit_submitButton: '',
    profile_passwordInvalidError: '',
  },
  onCreatePassword = () => {},
  onCTAClick = () => {},
  onFieldChange = e => e,
  showLinkToProfile,
  userEmail = '',
  utagAccountSource,
}) => {
  const { isGoogleSignIn } = useSelector(state => state?.app?.googleSignIn);
  const { isGoogleOneTap, googleOneTapEmail } = useSelector(state => state.app?.googleOneTap);
  const { googleEmail } = useSelector(state => state.app?.googleNewUser);
  const isGoogle = isGoogleOneTap || isGoogleSignIn;
  const googleEmailAddress = isGoogleOneTap ? googleOneTapEmail : googleEmail;

  const {
    profile_createNewPwInputPlaceholder: newPlaceholder = 'Password (6 Character min)',
    profile_createPasswordInfoSIWA: createPasswordInfo = "Set a password to enjoy your account perks on any device that doesn't support Apple login, or if you would like to login without Apple.",
    profile_createProfileLegal: createProfileLegal = `By clicking ${
      isGoogle ? CREATE_PASSWORD : 'Create Account'
    }, you agree to the [Terms of Use](https://www.fox.com/article/terms-of-use-597bc239ef528f0026dc0316), [Privacy Policy](https://www.fox.com/article/privacy-policy-597bbe4f6603660022fa8689), and agree to receive content and promotional communications from FOX.`,
    profile_createProfileButton: createButtonText = 'Create Account',
    profile_passwordInvalidError: passwordInvalidError,
  } = language || {};

  let createPasswordInputNew = React.createRef();

  const handleOnCreatePasswordInputNew = e => {
    onFieldChange(e);
    if (createPasswordInputNew.current) {
      createPasswordInputNew.current.focus();
    }
  };

  const passwordNewVisibility = createPasswordShowNewPassword ? 'text' : 'password';

  let passwordErrorText;
  let passwordNewErrorText;

  if (createPasswordPasswordError) {
    passwordErrorText = passwordInvalidError;
  }
  if (createPasswordNewPasswordError) {
    passwordNewErrorText = passwordInvalidError;
  }

  useEffect(() => {
    const pageProfileType = isGoogle
      ? SegmentPageProfileTypes.GOOGLE
      : SegmentPageProfileTypes.APPLE;
    handleAnalytics({
      event: isGoogle ? Events.PROFILE_GOOGLE_LINK_STARTED : Events.PROFILE_APPLE_ID_SIGNUP_STARTED,
      payload: {
        page_profile_type: pageProfileType,
        page_start_source: utagAccountSource,
      },
      tealiumPayload: {
        profile_type: pageProfileType,
        page_start_source: utagAccountSource,
        ...getCommonTealiumProps(),
      },
    });
  }, [utagAccountSource]);

  const handleFormSubmit = e => {
    e.preventDefault();
    onCreatePassword(isGoogle);
  };

  return (
    <main className={Style.createPassword}>
      <Header isGoogle={isGoogle} {...headerProps} />
      <form className={Style.createPasswordForm} onSubmit={handleFormSubmit}>
        <h1>{CREATE_PASSWORD}</h1>
        <div className={Style.createPasswordInfo}>
          {isGoogle
            ? `Set a password to enjoy your account perks on any device that doesn't support Google Sign-In, or if you would like to login without Google.`
            : createPasswordInfo}
        </div>
        <div className={Style.error}>{createPasswordGenericError}</div>
        <div className={Style.error}>{isGoogle && googleLinkingError}</div>
        <div className={Style.emailContainer}>
          <p className={Style.emailLabel}>{isGoogle ? googleEmailAddress : userEmail}</p>
        </div>
        <div className={Style.error}>{passwordErrorText}</div>
        <div className={Style.passwordContainer}>
          <input
            className={Style.inputField}
            type={passwordNewVisibility}
            name="createPasswordNewPassword"
            placeholder={newPlaceholder}
            value={createPasswordNewPassword || ''}
            onChange={onFieldChange}
            ref={input => {
              createPasswordInputNew = input;
            }}
            autoCapitalize="none"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          <label className={Style.passwordLabel}>
            <input
              className={Style.inputField}
              name="createPasswordShowNewPassword"
              type="checkbox"
              value={createPasswordShowNewPassword || ''}
              onChange={handleOnCreatePasswordInputNew}
            />
            <span />
          </label>
        </div>
        <div className={Style.error}>{passwordNewErrorText}</div>
        <div className={Style.legalTextContainer}>
          {helperMarkdownToComponent(createProfileLegal, null, {
            Paragraph: props => {
              if (!props?.children) return null;
              return <p className={Style.legalText}>{props?.children}</p>;
            },
            Link: props => (
              <a href={props?.href} className={Style.legalLink} target="noopener noreferrer">
                {props?.children}
              </a>
            ),
          })}
        </div>
        <button type="submit" className={Style.createProfileButton}>
          {isGoogle ? CREATE_PASSWORD : createButtonText}
        </button>
        {showLinkToProfile && (
          <div className={Style.ctaText} onClick={onCTAClick}>
            {isGoogle ? null : 'Link to existing FOX account'}
          </div>
        )}
      </form>
      <OverlayFooter language={language} />
    </main>
  );
};

CreatePassword.propTypes = {
  createPasswordGenericError: PropTypes.string,
  createPasswordNewPassword: PropTypes.string.isRequired,
  createPasswordNewPasswordError: PropTypes.bool,
  createPasswordPassword: PropTypes.string.isRequired,
  createPasswordPasswordError: PropTypes.bool,
  createPasswordShowNewPassword: PropTypes.bool,
  googleLinkingError: PropTypes.string,
  forgotPasswordCTAProps: PropTypes.shape({}),
  headerProps: PropTypes.shape({}).isRequired,
  language: PropTypes.shape({}).isRequired,
  onCreatePassword: PropTypes.func.isRequired,
  onCreatePasswordBackClick: PropTypes.func.isRequired,
  onCTAClick: PropTypes.func,
  onFieldChange: PropTypes.func.isRequired,
  showLinkToProfile: PropTypes.bool,
  userEmail: PropTypes.string,
  utagAccountSource: PropTypes.string,
};

export default CreatePassword;
